/**
 * Leviathan - Liftings List Page
 * Quasar Prime LLC
 *
 * List of terminal liftings (fuel purchases from terminals)
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus,
  Loader2,
  Search,
  Truck,
} from 'lucide-react';

import { API_URL } from '@/config/api';

interface LiftingItem {
  id: string;
  grossGallons: number;
  netGallons: number | null;
  product: {
    id: string;
    name: string;
    sku: string;
  };
}

interface Lifting {
  id: string;
  liftingNumber: string;
  status: string;
  scheduledDate: string;
  actualLoadTime: string | null;
  carrierName: string | null;
  driverName: string | null;
  truckNumber: string | null;
  bolNumber: string | null;
  grossGallonsLoaded: number | null;
  netGallonsLoaded: number | null;
  totalCost: number | null;
  terminal: {
    id: string;
    name: string;
    terminalCode: string;
    tcn: string | null;
  };
  items: LiftingItem[];
}

interface Terminal {
  id: string;
  name: string;
}

const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  EN_ROUTE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  AT_TERMINAL: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  LOADING: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  LOADED: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
  DEPARTED: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50',
  COMPLETED: 'bg-green-500/20 text-green-400 border-green-500/50',
  CANCELLED: 'bg-slate-500/20 text-slate-400 border-slate-500/50',
};

const STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  EN_ROUTE: 'En Route',
  AT_TERMINAL: 'At Terminal',
  LOADING: 'Loading',
  LOADED: 'Loaded',
  DEPARTED: 'Departed',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export default function LiftingsList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [terminalFilter, setTerminalFilter] = useState(searchParams.get('terminalId') || '');

  // Fetch terminals for filter
  const { data: terminalsData } = useQuery({
    queryKey: ['terminals-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/terminals?isActive=true&limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch terminals');
      return res.json();
    },
  });

  const terminals: Terminal[] = terminalsData?.items || [];

  // Fetch liftings
  const { data, isLoading } = useQuery({
    queryKey: ['liftings', search, statusFilter, terminalFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (terminalFilter) params.append('terminalId', terminalFilter);
      params.append('limit', '50');

      const res = await fetch(`${API_URL}/api/terminals/liftings/all?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch liftings');
      return res.json();
    },
  });

  const liftings: Lifting[] = data?.items || [];
  const total = data?.total || 0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Liftings</h1>
          <p className="text-slate-400 mt-1">
            {total} terminal BOLs - Fuel purchases from terminals
          </p>
        </div>
        <Link
          to="/liftings/new"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Lifting
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search liftings..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
          />
        </div>

        <select
          value={terminalFilter}
          onChange={(e) => setTerminalFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
        >
          <option value="">All Terminals</option>
          {terminals.map((terminal) => (
            <option key={terminal.id} value={terminal.id}>
              {terminal.name}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
        >
          <option value="">All Status</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Liftings Table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        ) : liftings.length === 0 ? (
          <div className="text-center py-12">
            <Truck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No liftings found</p>
            <Link to="/liftings/new" className="text-amber-400 hover:text-amber-300">
              Record your first lifting
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-900/50 border-b border-slate-700">
              <tr>
                <th className="text-left px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Lifting
                </th>
                <th className="text-left px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Terminal
                </th>
                <th className="text-left px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Products
                </th>
                <th className="text-right px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Net Gallons
                </th>
                <th className="text-right px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Amount
                </th>
                <th className="text-center px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Status
                </th>
                <th className="text-center px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {liftings.map((lifting) => (
                <tr
                  key={lifting.id}
                  onClick={() => navigate(`/liftings/${lifting.id}`)}
                  className="border-b border-slate-700/50 hover:bg-slate-800/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="text-amber-400 font-mono font-semibold">
                      {lifting.liftingNumber}
                    </div>
                    {lifting.bolNumber && (
                      <div className="text-slate-500 text-xs">BOL: {lifting.bolNumber}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-100 text-sm">{lifting.terminal.name}</div>
                    {lifting.terminal.tcn && (
                      <div className="text-slate-500 text-xs">{lifting.terminal.tcn}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-400 text-sm">
                      {lifting.items.map((i) => i.product.name).join(', ') || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-slate-100">
                      {lifting.netGallonsLoaded?.toLocaleString() || '-'}
                    </div>
                    {lifting.grossGallonsLoaded && (
                      <div className="text-slate-500 text-xs">
                        gross: {lifting.grossGallonsLoaded.toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {lifting.totalCost ? (
                      <div className="text-amber-400 font-semibold">
                        {formatCurrency(lifting.totalCost)}
                      </div>
                    ) : (
                      <div className="text-slate-500">-</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`px-2 py-1 text-xs border rounded ${
                        STATUS_COLORS[lifting.status] || STATUS_COLORS.SCHEDULED
                      }`}
                    >
                      {STATUS_LABELS[lifting.status] || lifting.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-400 text-sm">
                    {formatDate(lifting.scheduledDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
